import React, { useState, useRef } from 'react';
import { BookService } from '../services/bookService';

interface ImportBooksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

interface ImportResult {
  success: boolean;
  message: string;
  data?: {
    book: any;
    imported_contents: number;
    status: string;
  };
  errors?: any;
}

const ImportBooksModal: React.FC<ImportBooksModalProps> = ({
  isOpen,
  onClose,
  onImportSuccess
}) => {
  const [jsonData, setJsonData] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [validationErrors, setValidationErrors] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      setError('Vui lòng chọn file JSON hợp lệ');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);
        setJsonData(JSON.stringify(parsed, null, 2));
        setError(null);
        setValidationErrors(null);
      } catch (err) {
        setError('File JSON không hợp lệ. Vui lòng kiểm tra lại cú pháp.');
      }
    };
    reader.readAsText(file);
  };

  const handleJsonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonData(event.target.value);
    setError(null);
    setValidationErrors(null);
  };

  const validateJsonData = (): boolean => {
    if (!jsonData.trim()) {
      setError('Vui lòng nhập dữ liệu JSON hoặc chọn file');
      return false;
    }

    try {
      const parsed = JSON.parse(jsonData);
      
      // Basic validation for new format
      if (!parsed.title) {
        setError('Dữ liệu JSON thiếu trường bắt buộc: title');
        return false;
      }

      // Validate contents structure if present
      if (parsed.contents && Array.isArray(parsed.contents)) {
        for (let i = 0; i < parsed.contents.length; i++) {
          const content = parsed.contents[i];
          if (!content.id || !content.title || !content.type || content.order === undefined) {
            setError(`Nội dung thứ ${i + 1} thiếu các trường bắt buộc: id, title, type, order`);
            return false;
          }
          if (!['chapter', 'container', 'content'].includes(content.type)) {
            setError(`Nội dung thứ ${i + 1} có type không hợp lệ: ${content.type}. Phải là: chapter, container, hoặc content`);
            return false;
          }
        }
      }

      return true;
    } catch (err) {
      setError('Dữ liệu JSON không hợp lệ. Vui lòng kiểm tra lại cú pháp.');
      return false;
    }
  };

  const handleImport = async () => {
    if (!validateJsonData()) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setImportResult(null);
    setValidationErrors(null);

    try {
      const parsedData = JSON.parse(jsonData);
      const result = await BookService.importBookFromJson(parsedData, false);
      
      setImportResult(result);
      setSuccess('Import sách thành công!');
      
      // Reset form after successful import
      setTimeout(() => {
        setJsonData('');
        setSuccess(null);
        setImportResult(null);
        onImportSuccess();
        onClose();
      }, 2000);

    } catch (err: any) {
      console.error('Import error:', err);
      
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
        setError('Có lỗi validation trong dữ liệu JSON');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Có lỗi xảy ra khi import sách. Vui lòng thử lại.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setJsonData('');
    setError(null);
    setSuccess(null);
    setImportResult(null);
    setValidationErrors(null);
    onClose();
  };

  const createTemplate = () => {
    return {
        "title": "Sách mẫu - Tên sách",
        "name": "Sách mẫu - Tên sách",
        "description": "Mô tả sách mẫu",
        "category": "Giáo dục",
        "status": "Đã xuất bản",
        "author": "Tác giả mẫu",
        "version": "1.0",
        "price": 100000,
        "modules": [],
        "created_by": "user_id_here",
        "updated_at": new Date().toISOString(),
        "created_at": new Date().toISOString(),
        "cover_image_url": "https://example.com/cover.jpg",
        "update_ts": Math.floor(Date.now() / 1000),
        "updated_by": "user_id_here",
        "id": "book-template-id",
        "total_duration": null,
        "module_count": null,
        "section_count": 2,
        "contents": [
          {
            "title": "Chương 1 - Giới thiệu",
            "order": 1,
            "type": "chapter",
            "book_id": "book-template-id",
            "children": [
              {
                "title": "Tiết 1,2",
                "order": 1,
                "type": "container",
                "book_id": "book-template-id",
                "parent_section_id": "chapter-1-id",
                "content": [],
                "access": {
                  "visibility": "public",
                  "is_free_preview": true,
                  "unlock_conditions": []
                },
                "progress_rules": {
                  "completion_type": "viewed"
                },
                "estimates": {
                  "reading_time_min": 5
                },
                "content_block": {
                  "content_type": "html",
                  "payload": {
                    "html": "<p>Nội dung HTML mẫu.</p>"
                  }
                },
                "children": [
                  {
                    "title": "Nội dung mẫu",
                    "order": 1,
                    "type": "content",
                    "book_id": "book-template-id",
                    "parent_section_id": "container-1-id",
                    "content": {
                      "file": {
                        "name": "sample_file.pdf",
                        "type": "pdf",
                        "size": 1024000,
                        "url": "https://example.com/sample_file.pdf",
                        "id": "file-id-here"
                      }
                    },
                    "access": {
                      "visibility": "public",
                      "is_free_preview": true,
                      "unlock_conditions": []
                    },
                    "progress_rules": {
                      "completion_type": "viewed"
                    },
                    "estimates": {
                      "reading_time_min": 10
                    },
                    "content_block": {
                      "content_type": "pdf",
                      "payload": {
                        "file_url": "https://example.com/sample_file.pdf",
                        "page_from": 1,
                        "page_to": 10,
                        "viewer": {
                          "allow_download": false,
                          "start_page": 1
                        }
                      }
                    },
                    "created_by": "user_id_here",
                    "updated_at": new Date().toISOString(),
                    "created_at": new Date().toISOString(),
                    "updated_by": "user_id_here",
                    "id": "content-1-id"
                  }
                ],
                "created_by": "user_id_here",
                "updated_at": new Date().toISOString(),
                "created_at": new Date().toISOString(),
                "updated_by": "user_id_here",
                "id": "container-1-id",
                "file": null
              }
            ],
            "access": {
              "visibility": "public",
              "is_free_preview": true,
              "unlock_conditions": []
            },
            "parent_section_id": null,
            "created_by": "user_id_here",
            "updated_at": new Date().toISOString(),
            "created_at": new Date().toISOString(),
            "updated_by": "user_id_here",
            "id": "chapter-1-id"
          }
        ],
        "exportedAt": new Date().toISOString(),
        "exportedFrom": "iBook Admin System"
      };
  };

  const loadTemplate = async () => {
    try {
      const template = createTemplate();
      setJsonData(JSON.stringify(template, null, 2));
      setError(null);
      setValidationErrors(null);
    } catch (err) {
      console.error('Error loading template:', err);
      setError('Không thể tải template mẫu. Vui lòng thử lại.');
    }
  };

  const downloadTemplate = async () => {
    try {
      const template = createTemplate();
      
      const dataStr = JSON.stringify(template, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_book_new_format.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Không thể tải xuống template mẫu. Vui lòng thử lại.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Import Sách từ JSON
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Instructions - Hidden */}
          {/* <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Hướng dẫn:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Upload file JSON hoặc paste dữ liệu JSON vào textarea</li>
              <li>• Dữ liệu JSON phải có các trường bắt buộc: title, name, category, status, author, version</li>
              <li>• Nội dung (contents) phải có: id, title, type (chapter/container/content), order</li>
              <li>• Tải xuống template mẫu để tham khảo cấu trúc hoặc load trực tiếp vào form</li>
              <li>• Hệ thống sẽ tự động tạo ID mới cho tất cả nội dung (không cần lo về trùng lặp)</li>
            </ul>
          </div> */}

          {/* File Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload File JSON
            </label>
            <div className="flex items-center space-x-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <button
                onClick={loadTemplate}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 w-[200px]"
              >
                Load Template
              </button>
              <button
                onClick={downloadTemplate}
                className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 w-[250px]"
              >
                Download Template
              </button>
            </div>
          </div>

          {/* JSON Data Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dữ liệu JSON
            </label>
            <textarea
              value={jsonData}
              onChange={handleJsonChange}
              placeholder="Paste dữ liệu JSON vào đây..."
              className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Info about ID generation - Hidden */}
          {/* <div className="mb-6 p-4 bg-green-50 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Thông tin quan trọng</h3>
                <p className="text-sm text-green-700 mt-1">
                  Hệ thống sẽ tự động tạo ID mới cho tất cả nội dung khi import. 
                  Bạn có thể import cùng một file JSON nhiều lần mà không lo bị trùng lặp.
                </p>
              </div>
            </div>
          </div> */}

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Lỗi</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <h3 className="text-sm font-medium text-red-800 mb-2">Lỗi Validation:</h3>
              <ul className="text-sm text-red-700 space-y-1">
                {Object.entries(validationErrors).map(([field, errors]: [string, any]) => (
                  <li key={field}>
                    <strong>{field}:</strong> {Array.isArray(errors) ? errors.join(', ') : errors}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Success Display */}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex">
                <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Thành công</h3>
                  <p className="text-sm text-green-700 mt-1">{success}</p>
                  {importResult?.data && (
                    <div className="mt-2 text-sm text-green-700">
                      <p>• Sách: {importResult.data.book.title}</p>
                      <p>• Nội dung đã import: {importResult.data.imported_contents} mục</p>
                      <p>• Trạng thái: {importResult.data.status === 'created' ? 'Đã tạo mới' : 'Đã cập nhật'}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              onClick={handleImport}
              disabled={isLoading || !jsonData.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Đang import...
                </div>
              ) : (
                'Import Sách'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportBooksModal;
